import RolesModal, { RoleState } from '@app/components/userManagement/organizationUsersTable/RolesModal';
import { OrganizationalUser } from '@app/domain/userManagement/organizationUsers';
import { UserRoleType } from '@app/domain/userManagement/roleTypes';
import { UpdateWebUserRolesRequest } from '@app/domain/userManagement/users';
import Loadable from '@app/utils/Loadable';
import { Alert } from 'antd';
import React, { FC } from 'react';

interface Props {
  organizationalUsers: OrganizationalUser[];
  userRoleTypes: Loadable<UserRoleType[]>;
  saveRolesOfUsers(change: UpdateWebUserRolesRequest): Promise<void>;
  onDone(): void;
}

const MAX_NAMED_GLOBAL_ROLE_HOLDERS = 3;

const SelectedUsersRolesModal: FC<Props> = ({ organizationalUsers, userRoleTypes, saveRolesOfUsers, onDone }) => {
  function getRoleState(roleId: string, roleType: UserRoleType | undefined): RoleState {
    // Saving would touch only the organizational role, leaving the global one in place
    const globalRoleHolders = organizationalUsers.filter((organizationalUser) =>
      organizationalUser.globalAppRoleIds.includes(roleId),
    );
    if (globalRoleHolders.length) {
      return { isDisabled: true, tooltip: `${describeGlobalRoleHolders(globalRoleHolders)} cannot be changed here` };
    }

    // The organization can't be granted it, so it may only be taken away
    if (!roleType) {
      return {
        isDisabled: false,
        isRemovalOnly: true,
        tooltip: 'The organization does not have the service for this role, so it can only be removed',
      };
    }

    return { isDisabled: false, tooltip: null };
  }

  return (
    <RolesModal
      title={<>Permissions - {organizationalUsers.length} selected users</>}
      elementIds={{
        card: 'selected-users-roles-modal',
        search: 'selected-users-roles-search',
        table: 'selected-users-roles-table',
        close: 'btn-selected-users-roles-modal-close',
        cancel: 'btn-selected-users-roles-modal-cancel',
        save: 'btn-selected-users-roles-modal-save',
      }}
      contentColumnTitle='Roles'
      banner={
        <Alert
          type='info'
          showIcon
          message='A mixed role is held by only some of the selected users. Left mixed, it stays as is.'
        />
      }
      roleTypes={userRoleTypes}
      initialActiveRoleIdsPerUser={organizationalUsers.map((organizationalUser) => organizationalUser.allActiveAppRoleIds)}
      canEdit
      getRoleState={getRoleState}
      saveChange={saveRolesOfUsers}
      onDone={onDone}
    />
  );
};

function describeGlobalRoleHolders(globalRoleHolders: OrganizationalUser[]): string {
  const names = globalRoleHolders
    .slice(0, MAX_NAMED_GLOBAL_ROLE_HOLDERS)
    .map((organizationalUser) => organizationalUser.name ?? organizationalUser.tenantUserId);
  const othersCount = globalRoleHolders.length - names.length;

  return `A global role of ${names.join(', ')}${othersCount ? ` and ${othersCount} more` : ''}, so it`;
}

export default SelectedUsersRolesModal;
